// Line-based unified diff for the History view.
//
// We deliberately don't take a `diff` / `diff-match-patch` dependency — the
// frontend is otherwise plain react + react-markdown, and adding a 100KB
// diff library for a feature that only needs LCS-on-lines is not worth it.
// This file is ~80 lines and handles the markdown bodies we have.
//
// Algorithm: classic LCS dynamic programming on lines, then walk back to
// emit `same` / `remove` / `add` segments. Output is unified-diff-shaped
// (no context windows — we render every line, since cards are short
// enough that hiding context loses more than it saves).

export type DiffSegment =
  | { type: 'same'; lines: string[] }
  | { type: 'remove'; lines: string[] }
  | { type: 'add'; lines: string[] };

/**
 * Compute a unified line-diff between `before` and `after`. Each segment
 * is a contiguous run of identical / removed / added lines.
 *
 * Empty strings are treated as a single empty line ([""]) so the diff
 * always has a definite structure. A truly empty body (rev N has no body)
 * passes through as `before=""` → one empty `same`/`remove` line.
 */
export function lineDiff(before: string, after: string): DiffSegment[] {
  const a = before.split('\n');
  const b = after.split('\n');

  // LCS table: lcs[i][j] = length of LCS of a[0..i] and b[0..j].
  // n+1 × m+1; a[i] vs b[j] compared using strict equality on the line.
  const n = a.length;
  const m = b.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (a[i - 1] === b[j - 1]) {
        lcs[i][j] = lcs[i - 1][j - 1] + 1;
      } else {
        lcs[i][j] = Math.max(lcs[i - 1][j], lcs[i][j - 1]);
      }
    }
  }

  // Walk backwards from (n, m) to (0, 0), emitting per-cell action.
  type Op = 'same' | 'remove' | 'add';
  const ops: Array<{ op: Op; line: string }> = [];
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      ops.push({ op: 'same', line: a[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || lcs[i][j - 1] >= lcs[i - 1][j])) {
      ops.push({ op: 'add', line: b[j - 1] });
      j--;
    } else {
      ops.push({ op: 'remove', line: a[i - 1] });
      i--;
    }
  }
  ops.reverse();

  // Coalesce consecutive same-op entries into segments.
  const out: DiffSegment[] = [];
  for (const { op, line } of ops) {
    const last = out[out.length - 1];
    if (last && last.type === op) {
      last.lines.push(line);
    } else {
      out.push({ type: op, lines: [line] });
    }
  }
  return out;
}

/**
 * Helper for the History UI: returns true when two revisions differ ONLY in
 * `extra`. Used to default-fold rows where dismissed_gaps / promoted_at
 * metadata changed but the body/title/kind/pack are identical (those reads
 * as noise in a "what edits happened" view).
 *
 * `null` snapshots (op=delete row carries them, but historical pre-history
 * rows might not) compare equal-when-both-null, otherwise inequal.
 */
export function isExtraOnlyChange(
  prev: { kind: string | null; title: string | null; body: string | null; pack: string | null; extra: Record<string, unknown> | null },
  next: { kind: string | null; title: string | null; body: string | null; pack: string | null; extra: Record<string, unknown> | null },
): boolean {
  const sameContent =
    prev.kind === next.kind &&
    prev.title === next.title &&
    prev.body === next.body &&
    prev.pack === next.pack;
  if (!sameContent) return false;
  // Different extras and same content → extra-only.
  return JSON.stringify(prev.extra ?? {}) !== JSON.stringify(next.extra ?? {});
}
